import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  Power, Plus, Check, Users, Building2, MapPin, MoreHorizontal, Edit2, Trash2, RefreshCw, Loader2, Info,
} from 'lucide-react';
import apiService from '../services/api';
import { useApp } from '../context/AppContext';
import { DateRangePicker } from '../components/requests/DateRangePicker';
import { fmtRange, fmtDayCount } from '../utils/format';
import {
  cx, unwrapList, errorMessage, Button, IconButton, Chip, EmptyState, SkeletonRows, Notice, SectionHeader,
  inputClass, textareaClass, labelClass, Toggle, Segmented, Dropdown, Menu, Modal, ModalHeader, ModalBody,
  ModalFooter, ModalIcon, useConfirm,
} from './settingsUI';

// ═════════════════════════════════════════════════════════════
// OFFICE SHUTDOWNS
// A company closure (e.g. Christmas to New Year). Adding one books the closed
// working days off for everyone it applies to, so nobody enters the same leave
// by hand. Weekends, non-working days and public holidays aren't counted, days
// people already have off are left out, and people who join later are booked
// automatically (if that option is on).
// ═════════════════════════════════════════════════════════════

const unwrap = (res) => res?.data ?? res;

function scopeText(s, departments, locations) {
  if (s.appliesTo === 'departments') {
    const names = departments.filter(d => (s.departmentIds ?? []).includes(d.id)).map(d => d.name);
    return names.length ? names.join(', ') : 'Chosen departments';
  }
  if (s.appliesTo === 'locations') {
    const names = locations.filter(l => (s.locationIds ?? []).includes(l.id)).map(l => l.name);
    return names.length ? `People in ${names.join(', ')}` : 'Chosen locations';
  }
  return 'Everyone';
}

export default function ShutdownsSection() {
  const confirm = useConfirm();
  const { notifyLeaveChanged } = useApp() ?? {};
  const [list, setList] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [modal, setModal] = useState(null); // null | 'new' | shutdown
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const res = await apiService.getShutdowns();
      setList(unwrap(res) ?? []);
    } catch (e) {
      setList([]);
      toast.error(errorMessage(e, 'Couldn’t load office shutdowns.'));
    }
  }

  useEffect(() => {
    load();
    apiService.getDepartments().then(res => setDepartments(unwrapList(res, 'departments'))).catch(() => {});
    apiService.getHolidayLocations(new Date().getFullYear()).then(res => setLocations(unwrapList(res, 'locations'))).catch(() => {});
    apiService.getLeaveTypes().then(res => setLeaveTypes(unwrapList(res, 'leaveTypes'))).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (list ?? []).filter(s => String(s.to).slice(0, 10) >= today);
  const past = (list ?? []).filter(s => String(s.to).slice(0, 10) < today);

  function saved(row, summary) {
    setModal(null);
    load();
    notifyLeaveChanged?.();
    if (summary) {
      const bits = [`${summary.booked} ${summary.booked === 1 ? 'person' : 'people'} booked`];
      if (summary.removed) bits.push(`${summary.removed} booking${summary.removed === 1 ? '' : 's'} removed`);
      toast.success(`${row?.name ?? 'Shutdown'} saved · ${bits.join(' · ')}`);
    }
  }

  async function resync(s) {
    setBusyId(s.id);
    try {
      const res = await apiService.resyncShutdown(s.id);
      const summary = res?.summary;
      toast.success(summary?.created ? `Booked ${summary.created} more` : 'Everyone is already booked');
      load();
      notifyLeaveChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Couldn’t update the bookings.'));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(s) {
    const ok = await confirm({
      title: `Delete ${s.name}?`,
      message: `The ${s.peopleBooked ? `${s.peopleBooked} ` : ''}booking${s.peopleBooked === 1 ? '' : 's'} it made will be removed${s.deductsAllowance ? ', and the days go back on people’s allowance' : ''}.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(s.id);
    try {
      await apiService.deleteShutdown(s.id);
      toast.success(`${s.name} deleted`);
      load();
      notifyLeaveChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Couldn’t delete it.'));
    } finally {
      setBusyId(null);
    }
  }

  const card = (s) => (
    <div key={s.id} className="sd-card flex flex-wrap items-center gap-4 rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sand-100 text-sand-700">
        <Power size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold text-sand-900">{s.name}</div>
        <div className="mt-0.5 text-[13px] text-sand-600">{fmtRange(s.from, s.to)} · {scopeText(s, departments, locations)}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {s.deductsAllowance
            ? <Chip tone="amber">From allowance{s.leaveType?.name ? ` · ${s.leaveType.name}` : ''}</Chip>
            : <Chip tone="green">Paid closure</Chip>}
          <Chip tone="sand"><Users size={10} /> {s.peopleBooked} booked</Chip>
          {s.peopleExcluded > 0 && <Chip tone="sand">{s.peopleExcluded} taken out</Chip>}
          {s.includeNewStarters && <Chip tone="sand">Includes new starters</Chip>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {busyId === s.id && <Loader2 size={16} className="animate-spin text-sand-500" />}
        <Menu
          trigger={({ toggle }) => <IconButton label={`Actions for ${s.name}`} onClick={toggle}><MoreHorizontal size={17} /></IconButton>}
          items={[
            { label: 'Edit', icon: Edit2, onClick: () => setModal(s) },
            { label: 'Book anyone missing', hint: 'New starters, or people moved into it', icon: RefreshCw, onClick: () => resync(s) },
            { label: 'Delete', icon: Trash2, tone: 'danger', hint: 'Removes the bookings it made', onClick: () => remove(s) },
          ]}
        />
      </div>
    </div>
  );

  return (
    <div>
      <style>{`.sd-card { position: relative; } .sd-card:hover, .sd-card:focus-within { z-index: 20; }`}</style>
      <SectionHeader
        title="Office shutdowns"
        description="When the office closes (e.g. Christmas to New Year), add it here and the days are booked off for everyone it applies to."
        action={<Button variant="primary" icon={Plus} onClick={() => setModal('new')}>Add shutdown</Button>}
      />

      {list === null ? <SkeletonRows rows={2} height="h-20" /> : list.length === 0 ? (
        <EmptyState icon={Power} title="No office shutdowns" message="Add one and everyone it applies to is booked off automatically, with weekends and public holidays left out.">
          <Button variant="primary" icon={Plus} onClick={() => setModal('new')}>Add shutdown</Button>
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && <div className="space-y-2">{upcoming.map(card)}</div>}
          {past.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">Past</div>
              <div className="space-y-2 opacity-75">{past.map(card)}</div>
            </div>
          )}
        </div>
      )}

      {/* Rendered on <body>: the settings panel is animated, which would make the
          full-screen overlay sit inside the panel and crop the top of the dialog */}
      {modal && createPortal(
        <ShutdownModal
          shutdown={modal === 'new' ? null : modal}
          departments={departments}
          locations={locations}
          leaveTypes={leaveTypes}
          onClose={() => setModal(null)}
          onSaved={saved}
        />,
        document.body,
      )}
    </div>
  );
}

function CheckList({ items, selected, onChange, empty }) {
  if (!items.length) return <p className="m-0 text-[13px] text-sand-600">{empty}</p>;
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(it => {
        const on = selected.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(it.id)}
            className={cx(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
              on ? 'border-ink bg-ink text-[color:var(--on-accent)]' : 'border-sand-200 bg-[color:var(--surface)] text-sand-700 hover:border-sand-300',
            )}
          >
            {on && <Check size={13} />}{it.name}
          </button>
        );
      })}
    </div>
  );
}

function ShutdownModal({ shutdown, departments, locations, leaveTypes, onClose, onSaved }) {
  const editing = !!shutdown;
  const { holidayWord } = useApp() ?? {};
  // Public holidays (this year and next) shown on the date picker
  const [holidays, setHolidays] = useState([]);
  useEffect(() => {
    const y = new Date().getFullYear();
    Promise.all([apiService.getPublicHolidays(y).catch(() => null), apiService.getPublicHolidays(y + 1).catch(() => null)])
      .then(([a, b]) => setHolidays([...unwrapList(a, 'holidays'), ...unwrapList(b, 'holidays')]));
  }, []);
  const balanceTypes = useMemo(() => leaveTypes.filter(t => t.affectsBalance !== false && t.isActive !== false), [leaveTypes]);
  const [form, setForm] = useState(() => ({
    name: shutdown?.name ?? '',
    from: shutdown?.from ? String(shutdown.from).slice(0, 10) : '',
    to: shutdown?.to ? String(shutdown.to).slice(0, 10) : '',
    appliesTo: shutdown?.appliesTo ?? 'all',
    departmentIds: shutdown?.departmentIds ?? [],
    locationIds: shutdown?.locationIds ?? [],
    deductsAllowance: shutdown?.deductsAllowance ?? true,
    leaveTypeId: shutdown?.leaveTypeId ?? '',
    includeNewStarters: shutdown?.includeNewStarters ?? true,
    note: shutdown?.note ?? '',
  }));
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPeople, setShowPeople] = useState(false);

  // Focus the name without scrolling: the browser's own autofocus could scroll
  // the dialog itself (it clips its overflow) and hide the title at the top
  const nameRef = useRef(null);
  useEffect(() => {
    if (editing) return undefined;
    const t = setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 50);
    return () => clearTimeout(t);
  }, [editing]);

  // Default leave type: Annual Leave
  useEffect(() => {
    if (form.leaveTypeId || !balanceTypes.length) return;
    const annual = balanceTypes.find(t => /annual/i.test(t.name)) ?? balanceTypes[0];
    setForm(f => ({ ...f, leaveTypeId: annual.id }));
  }, [balanceTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = useMemo(() => ({
    id: shutdown?.id,
    name: form.name.trim() || 'Office shutdown',
    from: form.from,
    to: form.to || form.from,
    appliesTo: form.appliesTo,
    departmentIds: form.appliesTo === 'departments' ? form.departmentIds : [],
    locationIds: form.appliesTo === 'locations' ? form.locationIds : [],
    deductsAllowance: form.deductsAllowance,
    leaveTypeId: form.deductsAllowance ? (form.leaveTypeId || null) : null,
    includeNewStarters: form.includeNewStarters,
    note: form.note.trim() || null,
  }), [form, shutdown]);

  const scopeReady = form.appliesTo === 'all'
    || (form.appliesTo === 'departments' && form.departmentIds.length > 0)
    || (form.appliesTo === 'locations' && form.locationIds.length > 0);
  const ready = !!form.from && !!(form.to || form.from) && scopeReady;

  // Who will be booked, as the form changes
  useEffect(() => {
    if (!ready) { setPreview(null); setPreviewErr(''); return undefined; }
    let live = true;
    setChecking(true);
    const t = setTimeout(() => {
      apiService.previewShutdown(payload)
        .then(res => { if (live) { setPreview(unwrap(res)); setPreviewErr(''); } })
        .catch(e => { if (live) { setPreview(null); setPreviewErr(errorMessage(e, 'Couldn’t check who this applies to.')); } })
        .finally(() => { if (live) setChecking(false); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [payload, ready]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Give the shutdown a name'); return; }
    if (!ready) { toast.error(scopeReady ? 'Choose the dates' : 'Choose who it applies to'); return; }
    setSaving(true);
    try {
      const body = { ...payload, name: form.name.trim() };
      const res = editing ? await apiService.updateShutdown(shutdown.id, body) : await apiService.createShutdown(body);
      onSaved(unwrap(res), res?.summary);
    } catch (err) {
      toast.error(errorMessage(err, 'Couldn’t save the shutdown.'));
    } finally {
      setSaving(false);
    }
  }

  const people = preview?.perPerson ?? [];
  const daysList = [...new Set(people.map(p => p.days))];
  const typeOptions = balanceTypes.map(t => ({ value: t.id, label: t.name }));

  return (
    <Modal onClose={onClose} className="max-w-2xl" z={200} labelledBy="sd-modal-title">
      {/* The title and buttons stay put; only the middle scrolls */}
      <style>{`[role="dialog"]:has(#sd-modal-title) { overflow: clip; }`}</style>
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" style={{ overflow: 'clip' }}>
        <ModalHeader
          id="sd-modal-title"
          title={editing ? 'Edit office shutdown' : 'Add office shutdown'}
          subtitle="The closed days are booked off for everyone it applies to"
          onClose={onClose}
          leading={<ModalIcon icon={Power} />}
        />
        <ModalBody className="space-y-5">
          <div>
            <label htmlFor="sd-name" className={labelClass}>Name</label>
            <input id="sd-name" ref={nameRef} className={inputClass} value={form.name} maxLength={120}
              onChange={e => set('name')(e.target.value)} placeholder="e.g. Christmas closure" />
          </div>

          <div>
            <span className={labelClass}>Dates</span>
            <DateRangePicker
              from={form.from}
              to={form.to}
              onChange={({ from, to }) => setForm(f => ({ ...f, from, to }))}
              holidays={holidays}
              holidayWord={holidayWord?.() ?? 'Public holiday'}
            />
            {form.from && (
              <p className="mt-2 text-[13px] text-sand-700">
                {form.to ? fmtRange(form.from, form.to) : `${fmtRange(form.from, form.from)} — pick the last day, or leave it for a single day`}
              </p>
            )}
          </div>

          <div>
            <span className={labelClass}>Applies to</span>
            <Segmented
              size="md"
              className="flex w-full"
              value={form.appliesTo}
              onChange={set('appliesTo')}
              options={[
                { value: 'all', label: 'Everyone', icon: Users },
                { value: 'departments', label: 'Departments', icon: Building2 },
                { value: 'locations', label: 'Locations', icon: MapPin },
              ]}
            />
            {form.appliesTo === 'departments' && (
              <div className="mt-3">
                <CheckList items={departments} selected={form.departmentIds} onChange={set('departmentIds')} empty="No departments yet. Add them in Departments." />
              </div>
            )}
            {form.appliesTo === 'locations' && (
              <div className="mt-3">
                <CheckList items={locations} selected={form.locationIds} onChange={set('locationIds')} empty="No holiday locations yet. Add them in Public holidays." />
              </div>
            )}
          </div>

          <div>
            <span className={labelClass}>How it’s counted</span>
            <Segmented
              size="md"
              className="flex w-full"
              value={form.deductsAllowance ? 'deduct' : 'paid'}
              onChange={v => set('deductsAllowance')(v === 'deduct')}
              options={[
                { value: 'deduct', label: 'Take from allowance' },
                { value: 'paid', label: 'Paid closure' },
              ]}
            />
            <p className="mt-2 text-[13px] text-sand-600">
              {form.deductsAllowance
                ? 'Each person’s working days in the shutdown come off their allowance, the same as booking the leave themselves.'
                : 'The company gives these days: they show as “Office Closed” and nothing comes off anyone’s allowance.'}
            </p>
            {form.deductsAllowance && typeOptions.length > 1 && (
              <div className="mt-3 max-w-xs">
                <label className={labelClass}>Book as</label>
                <Dropdown options={typeOptions} value={form.leaveTypeId} onChange={set('leaveTypeId')} />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 rounded-2xl border border-sand-200 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-sand-900">Book new starters automatically</div>
              <div className="text-xs text-sand-600">People who join, or move into it, before it ends are booked too. Leavers are taken off.</div>
            </div>
            <Toggle checked={form.includeNewStarters} onChange={set('includeNewStarters')} label={form.includeNewStarters ? 'On' : 'Off'} />
          </div>

          <div>
            <label htmlFor="sd-note" className={labelClass}>Note <span className="font-normal text-sand-500">(optional, for admins)</span></label>
            <textarea id="sd-note" rows={2} className={textareaClass} value={form.note} maxLength={1000}
              onChange={e => set('note')(e.target.value)} placeholder="e.g. Agreed with the board in September" />
          </div>

          {/* Who will be booked */}
          {ready && (
            <div aria-live="polite" className="rounded-2xl border border-sand-200 bg-sand-50 p-4 text-[13px] text-sand-700">
              <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">
                {checking ? <Loader2 size={12} className="animate-spin" /> : <Info size={12} />} {editing ? 'After saving' : 'What happens'}
              </div>
              {previewErr && <Notice tone="warn">{previewErr}</Notice>}
              {preview && (
                <div className="space-y-2" style={{ opacity: checking ? 0.6 : 1 }}>
                  <p className="m-0 text-sand-900">
                    <span className="font-semibold">{preview.booked} {preview.booked === 1 ? 'person' : 'people'}</span> booked
                    {daysList.length === 1 && daysList[0] > 0 ? <>, <span className="font-semibold">{fmtDayCount(daysList[0])}</span> each</> : ''}
                    {' '}as {preview.leaveType?.name ?? (form.deductsAllowance ? 'leave' : 'Office Closed')}.
                    {' '}Weekends, non-working days and public holidays aren’t counted.
                  </p>
                  {editing && (preview.created > 0 || preview.removed > 0) && (
                    <p className="m-0">{preview.created} booking{preview.created === 1 ? '' : 's'} added, {preview.removed} removed.</p>
                  )}
                  {preview.alreadyOff?.length > 0 && (
                    <p className="m-0">Already off for some of it, so only the other days are booked: {preview.alreadyOff.map(p => p.name).join(', ')}.</p>
                  )}
                  {preview.excluded?.length > 0 && (
                    <p className="m-0">Taken out by an admin (won’t be booked again): {preview.excluded.map(p => p.name).join(', ')}.</p>
                  )}
                  {preview.belowZero?.length > 0 && (
                    <Notice tone="warn">
                      This takes {preview.belowZero.length === 1 ? 'one person' : `${preview.belowZero.length} people`} below zero: {preview.belowZero.map(p => `${p.name} (${fmtDayCount(p.after)})`).join(', ')}.
                      They’re still booked, and marked “beyond balance”.
                    </Notice>
                  )}
                  {people.length > 0 && (
                    <div>
                      <button type="button" className="text-[13px] font-semibold text-[color:var(--accent)] underline-offset-2 hover:underline" onClick={() => setShowPeople(v => !v)} aria-expanded={showPeople}>
                        {showPeople ? 'Hide people' : `Show the ${people.length} ${people.length === 1 ? 'person' : 'people'}`}
                      </button>
                      {showPeople && (
                        <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {people.map(p => (
                            <li key={p.id} className="flex justify-between gap-3">
                              <span className="truncate">{p.name}</span>
                              <span className="shrink-0 text-sand-600">{p.days > 0 ? fmtDayCount(p.days) : 'not working'}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-end">
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" icon={Check} loading={saving} disabled={!ready}>
            {editing ? 'Save and update bookings' : 'Add and book everyone'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}